import React, { useState, useEffect, useCallback, useRef } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import PlayArrowIcon from "@material-ui/icons/PlayArrowOutlined";
import StopIcon from "@material-ui/icons/StopOutlined";
import CloudUploadIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useAudioPreview } from "../../utils/audioPreview";

const useStyles = makeStyles(theme => ({
  content: { minHeight: 280 },
  tab: {
    minWidth: 0,
    flex: 1,
    fontSize: 12,
    padding: theme.spacing(0, 0.5),
  },
  list: { maxHeight: "44vh", overflow: "auto", marginTop: theme.spacing(1) },
  track: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: theme.spacing(1, 1),
    borderRadius: 8,
    border: `1px solid ${theme.app.border}`,
    marginBottom: 6,
    cursor: "pointer",
    "&:hover": { borderColor: theme.app.lime, background: theme.app.selectedRow },
  },
  trackName: { flex: 1, fontWeight: 600, fontSize: 13 },
  trackCat: { fontSize: 11, color: theme.app.textTertiary },
  hint: { color: theme.app.textTertiary, fontSize: 13, padding: theme.spacing(3, 1), textAlign: "center" },
  drop: {
    border: `2px dashed ${theme.app.borderInput}`,
    borderRadius: 10,
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.app.textSecondary,
    cursor: "pointer",
    marginTop: theme.spacing(2),
  },
}));

const AudioPicker = ({ open, value, kind, onClose, onPick }) => {
  const classes = useStyles();
  const [tab, setTab] = useState(0);
  const [tracks, setTracks] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [storageReady, setStorageReady] = useState(false);
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { playingUrl, toggle, stop } = useAudioPreview();

  const loadLibrary = useCallback(async () => {
    try {
      const { data } = await api.get("/audio/library", {
        params: kind ? { kind } : {},
      });
      setTracks(data.tracks || []);
      setUploads(data.uploads || []);
      setStorageReady(!!data.storageReady);
    } catch (err) {
      toastError(err);
    }
  }, [kind]);

  useEffect(() => {
    if (open) {
      setUrl(value || "");
      loadLibrary();
    }
  }, [open, value, loadLibrary]);

  const handleClose = () => {
    stop();
    onClose();
  };

  const pick = u => {
    if (!u) return;
    stop();
    onPick(u);
    onClose();
  };

  const preview = (e, u) => {
    e.stopPropagation();
    toggle(u);
  };

  const removeUpload = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/audio/uploads/${id}`);
      setUploads(list => list.filter(x => x.id !== id));
    } catch (err) {
      toastError(err);
    }
  };

  const onFile = async e => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/audio/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      pick(data.url);
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Escolher música / áudio</DialogTitle>
      <DialogContent className={classes.content}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab className={classes.tab} label="Biblioteca" />
          <Tab className={classes.tab} label="Meus áudios" />
          <Tab className={classes.tab} label="Upload" />
          <Tab className={classes.tab} label="URL" />
        </Tabs>

        {tab === 0 && (
          <div className={classes.list}>
            {tracks.length === 0 ? (
              <Typography className={classes.hint}>
                Biblioteca em preparação. Por enquanto use Upload ou URL.
              </Typography>
            ) : (
              tracks.map(t => (
                <div key={t.id} className={classes.track} onClick={() => pick(t.url)}>
                  <IconButton size="small" onClick={e => preview(e, t.url)} title={playingUrl === t.url ? "Parar" : "Ouvir"}>
                    {playingUrl === t.url ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                  <span className={classes.trackName}>{t.name}</span>
                  <span className={classes.trackCat}>{t.category}</span>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 1 && (
          <div className={classes.list}>
            {uploads.length === 0 ? (
              <Typography className={classes.hint}>
                Você ainda não enviou nenhum áudio. Use a aba Upload — seus arquivos
                ficam salvos aqui para reaproveitar em outros nós.
              </Typography>
            ) : (
              uploads.map(u => (
                <div key={u.id} className={classes.track} onClick={() => pick(u.url)}>
                  <IconButton size="small" onClick={e => preview(e, u.url)} title={playingUrl === u.url ? "Parar" : "Ouvir"}>
                    {playingUrl === u.url ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                  <span className={classes.trackName}>{u.name}</span>
                  <IconButton size="small" onClick={e => removeUpload(e, u.id)} title="Excluir">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 2 && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,audio/wav,.mp3,.wav"
              style={{ display: "none" }}
              onChange={onFile}
            />
            <div className={classes.drop} onClick={() => !uploading && fileRef.current && fileRef.current.click()}>
              {uploading ? (
                <CircularProgress size={28} />
              ) : (
                <>
                  <CloudUploadIcon fontSize="large" />
                  <Typography>Clique para enviar um .mp3 ou .wav (até 15 MB)</Typography>
                </>
              )}
            </div>
            {!storageReady && (
              <Typography className={classes.hint}>
                Armazenamento ainda não configurado — o upload ficará disponível quando o bucket estiver pronto.
              </Typography>
            )}
          </div>
        )}

        {tab === 3 && (
          <div style={{ marginTop: 16 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="URL do áudio (.mp3/.wav)"
              value={url}
              onChange={e => {
                const v = e.target.value;
                setUrl(v);
              }}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        {tab === 3 && (
          <Button color="primary" variant="contained" disabled={!url.trim()} onClick={() => pick(url.trim())}>
            Usar URL
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AudioPicker;
